import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Divider,
    Drawer,
    Grid,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { CREATE, DataAPIContext } from '../api';
import FixedLinearProgress from '../components/FixedLinearProgress';
import DeleteIconButton from '../components/DeleteIconButton';
import ImageActionButton from '../components/ImageActionButton';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import useLeavePrompt from '../components/useLeavePrompt';
import { ContextStore } from '../store/contextStore';
import { toCoord } from './utils';

const MAX_STOPS = 3;

// What the leave prompt compares against the empty form.
const formSnapshot = (f) => ({
    name: f.name,
    description: f.description,
    latitude: toCoord(f.latitude),
    longitude: toCoord(f.longitude),
    category: f.category?.id ?? null,
    tourOperator: f.tourOperator,
    imageChanged: f.imageFile !== null,
    stops: f.stops.map((s) => ({ id: s.id ?? null, distance: s.distance })),
});

const AttractionCreate = ({ onCreated }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [latitude, setLatitude] = React.useState('');
    const [longitude, setLongitude] = React.useState('');
    const [category, setCategory] = React.useState(null);
    const [tourOperator, setTourOperator] = React.useState(currentLicensee || null);
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState(null);
    // stops: [{ id, name, distance }] — distance is a string (user input).
    const [stops, setStops] = React.useState([]);
    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});

    const form = { name, description, latitude, longitude, category, tourOperator, imageFile, stops };
    const [initialForm] = React.useState(() => formSnapshot(form));
    const hasChanges = !isEqual(formSnapshot(form), initialForm);
    const leaveWithoutPrompt = useLeavePrompt(hasChanges);

    const onLicenseeChange = (id) => {
        setTourOperator(id);
    };

    // X, CANCEL and the backdrop all land here; the leave prompt asks first.
    const close = () => history.push('/bus-tours/attractions');

    const onPickImage = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    const addStopRow = () => {
        if (stops.length >= MAX_STOPS) return;
        setStops((prev) => [...prev, { id: null, name: '', distance: '' }]);
    };

    const updateStop = (index, patch) => {
        setStops((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    const removeStop = (index) => {
        setStops((prev) => prev.filter((_, i) => i !== index));
    };

    const onSave = () => {
        if (isFranchise && !tourOperator) {
            setErrors({ tour_operator: { error: true, helperText: 'Licensee is required' } });
            return;
        }
        setErrors({});
        const body = {
            name,
            description: description || null,
            latitude: latitude !== '' ? parseFloat(latitude) : null,
            longitude: longitude !== '' ? parseFloat(longitude) : null,
            category,
            tour_operator: tourOperator,
            stops: stops
                .filter((s) => s.id != null)
                .map((s) => ({
                    id: s.id,
                    distance: s.distance !== '' ? parseFloat(s.distance) : null,
                })),
        };
        const params = imageFile
            ? { data: body, files: { image: imageFile }, upload: true }
            : { data: body };

        // Success navigates away and unmounts the form, so only failures reset `saving`.
        setSaving(true);
        dataAPI(CREATE, 'attraction', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error creating attraction', { variant: 'error' });
                    setSaving(false);
                } else {
                    enqueueSnackbar('Attraction created', { variant: 'success' });
                    if (onCreated) onCreated();
                    leaveWithoutPrompt(close);
                }
            })
            .catch(() => {
                enqueueSnackbar('Error creating attraction', { variant: 'error' });
                setSaving(false);
            });
    };

    return (
        <>
            <PageMeta title="Create New Attraction" />
            <FixedLinearProgress visibility={saving} />
            <Drawer
                anchor="right"
                open
                onClose={close}
                PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            px: 2,
                            py: 2.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>Create New Attraction</Typography>
                        <IconButton onClick={close} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Body */}
                    <FormLicenseeContext.Provider value={tourOperator}>
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <LicenseeFormField
                            value={tourOperator}
                            onChange={onLicenseeChange}
                            error={errors.tour_operator?.error}
                            helperText={errors.tour_operator?.helperText}
                        />
                        <TextField
                            variant="filled"
                            label="Attraction Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            variant="filled"
                            label="Description (Optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    variant="filled"
                                    label="Latitude"
                                    type="number"
                                    value={latitude}
                                    onChange={(e) => setLatitude(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    variant="filled"
                                    label="Longitude"
                                    type="number"
                                    value={longitude}
                                    onChange={(e) => setLongitude(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                        </Grid>

                        <RemoteAutocomplete
                            resource="activity_category"
                            urlSuffix={null}
                            label="Category"
                            variant="filled"
                            size="small"
                            fullWidth
                            value={category}
                            onChange={(ev, value) => setCategory(value || null)}
                            getOptionLabel={(opt) => opt?.title || ''}
                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                            optional
                        />

                        {imagePreview ? (
                            <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                <Image
                                    src={imagePreview}
                                    cover
                                    height={200}
                                    width="100%"
                                    style={{ borderRadius: 8, display: 'block' }}
                                />
                                <Box sx={{ position: 'absolute', top: 11, right: 11 }}>
                                    <DeleteIconButton title="Delete image" overImage onClick={clearImage} />
                                </Box>
                                <ImageActionButton hasImage overImage component="label" sx={{ position: 'absolute', bottom: 12, right: 12 }}>
                                    <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                </ImageActionButton>
                            </Box>
                        ) : (
                            <ImageActionButton component="label" sx={{ alignSelf: 'flex-start' }}>
                                <input type="file" hidden accept="image/*" onChange={onPickImage} />
                            </ImageActionButton>
                        )}

                        <Divider />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontSize: 18, fontWeight: 500 }}>
                                Nearest bus stops (up to {MAX_STOPS})
                            </Typography>
                            {stops.map((s, idx) => (
                                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <RemoteAutocomplete
                                            resource="route_stop"
                                            urlSuffix={null}
                                            label="Bus Stop"
                                            variant="filled"
                                            size="small"
                                            value={s.id != null ? { id: s.id, name: s.name } : null}
                                            onChange={(ev, value) =>
                                                updateStop(idx, {
                                                    id: value?.id ?? null,
                                                    name: value?.name || '',
                                                })
                                            }
                                            getOptionLabel={(opt) => opt?.name || ''}
                                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                            optional
                                        />
                                    </Box>
                                    <TextField
                                        variant="filled"
                                        label="Distance (m)"
                                        type="number"
                                        value={s.distance}
                                        onChange={(e) => updateStop(idx, { distance: e.target.value })}
                                        size="small"
                                        sx={{ width: 100 }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <DeleteIconButton title="Remove stop" onClick={() => removeStop(idx)} />
                                </Box>
                            ))}
                            {stops.length < MAX_STOPS && (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={addStopRow}
                                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                                >
                                    ADD BUS STOP
                                </Button>
                            )}
                        </Box>
                    </Box>
                    </FormLicenseeContext.Provider>

                    {/* Footer */}
                    <Box
                        sx={{
                            borderTop: '1px solid #d8d8d8',
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            zIndex: 2,
                        }}
                    >
                        <FormActionButtons
                            onSubmit={onSave}
                            submitDisabled={saving || !name}
                            onCancel={close}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default AttractionCreate;
